import { addCurrentUserEventListener, getCurrentUser } from '../api/current-user';
import type { IUserSettings } from './IUserSettings';
import { userLocalSettings } from './UserSettingsContext';

/** Tab strip placements offered as a user preference ('top' is the site default). */
export type PreferredTabPlacement = 'top' | 'start';
/** Tab strip visual styles offered as a user preference ('classic' is the site default). */
export type PreferredTabVariant = 'classic' | 'chip';

/**
 * Per-user UI preferences that drive component defaults site-wide.
 * A component only falls back to these when its caller did not configure the
 * value explicitly, so page-specific overrides always win.
 */
export interface UserUIPreferences {
  /** Default rows per page for data tables (DataTableWrapper/LitTableWrapper.pageLength). */
  tableRowsPerPage: number;
  /** Default tab strip placement for TabManager pages. */
  tabPlacement: PreferredTabPlacement;
  /** Default tab strip visual style for TabManager pages. */
  tabVariant: PreferredTabVariant;
  /** Draw data tables as a raised card with a drop shadow (DataTableWrapper.showTableShadow). */
  tableElevated: boolean;
  /** Draw vertical borders between table cells, not just horizontal row dividers (DataTableWrapper.showCellBorders). */
  tableCellBorders: boolean;
  /** Uppercase data-table header labels (DataTableWrapper.uppercaseHeaderText). */
  tableUppercaseHeader: boolean;
}

export const defaultUserUIPreferences: Readonly<UserUIPreferences> = Object.freeze({
  tableRowsPerPage: 25,
  tabPlacement: 'top',
  tabVariant: 'classic',
  // Match the flat legacy-table look the site shipped with; a user opts into any of these.
  tableElevated: false,
  tableCellBorders: false,
  tableUppercaseHeader: false
});

/** Rows-per-page choices offered by the preferences dialog. */
export const rowsPerPageChoices: readonly number[] = Object.freeze([10, 25, 50, 100]);

/**
 * Coerce untrusted preference data (localStorage can hold anything: hand
 * edits, values from older/newer app versions, corruption) into a safe
 * UserUIPreferences. Invalid fields fall back to the defaults - a bad
 * tableRowsPerPage would otherwise reach every table's pageLength and produce
 * pageCount = Infinity / NaN and broken server fetches.
 */
export function sanitizeUserUIPreferences(input: Partial<UserUIPreferences> | null | undefined): UserUIPreferences {
  const defaults = defaultUserUIPreferences;
  const rows = Number(input?.tableRowsPerPage);
  return {
    tableRowsPerPage: Number.isInteger(rows) && rows >= 1 && rows <= 1000 ? rows : defaults.tableRowsPerPage,
    tabPlacement:
      input?.tabPlacement === 'top' || input?.tabPlacement === 'start' ? input.tabPlacement : defaults.tabPlacement,
    tabVariant:
      input?.tabVariant === 'classic' || input?.tabVariant === 'chip' ? input.tabVariant : defaults.tabVariant,
    tableElevated: typeof input?.tableElevated === 'boolean' ? input.tableElevated : defaults.tableElevated,
    tableCellBorders: typeof input?.tableCellBorders === 'boolean' ? input.tableCellBorders : defaults.tableCellBorders,
    tableUppercaseHeader:
      typeof input?.tableUppercaseHeader === 'boolean' ? input.tableUppercaseHeader : defaults.tableUppercaseHeader
  };
}

/**
 * Persistence behind {@link UserPreferencesStore}. Local-only today; the async
 * shape exists so a backend-backed implementation (roaming preferences across
 * sessions/devices) can be swapped in via {@link UserPreferencesStore.setStorage}
 * without touching any consumer.
 */
export interface IUserPreferencesStorage {
  load(): Partial<UserUIPreferences> | null | Promise<Partial<UserUIPreferences> | null>;
  save(preferences: UserUIPreferences): void | Promise<void>;
}

/** Default storage: the browser's localStorage via the shared IUserSettings wrapper. */
export class LocalUserPreferencesStorage implements IUserPreferencesStorage {
  private readonly settings: IUserSettings;

  constructor(settings: IUserSettings = userLocalSettings) {
    this.settings = settings;
  }

  /**
   * Keyed per signed-in user so different logins on the same browser do not
   * see or overwrite each other's preferences. Resolved on every call: the
   * store re-reads through here when the current user changes.
   */
  private get key(): string {
    return `user-ui-preferences:${getCurrentUser()?.id ?? 'anonymous'}`;
  }

  load(): Partial<UserUIPreferences> | null {
    return this.settings.getSetting<Partial<UserUIPreferences>>(this.key);
  }

  save(preferences: UserUIPreferences): void {
    this.settings.saveSetting(this.key, preferences);
  }
}

/** Window event fired (with `detail.preferences`) whenever the preferences change. */
export const userPreferencesChangedEventName = 'wm-user-preferences-changed';

export type UserPreferencesChangedEvent = CustomEvent<{ preferences: Readonly<UserUIPreferences> }>;

export class UserPreferencesStore {
  private storage: IUserPreferencesStorage;
  private _current: UserUIPreferences = { ...defaultUserUIPreferences };

  constructor(storage: IUserPreferencesStorage) {
    this.storage = storage;
    void this.reload(false);
    // Re-read (and broadcast) whenever a different user signs in on this browser,
    // so one login never keeps another login's preferences.
    addCurrentUserEventListener({ userStateChanged: () => void this.reload() });
  }

  /** The effective preferences (defaults merged with whatever storage holds). */
  public get current(): Readonly<UserUIPreferences> {
    return this._current;
  }

  /** Merge the given changes, persist, and notify listeners if anything changed. */
  public async update(changes: Partial<UserUIPreferences>): Promise<void> {
    const next = sanitizeUserUIPreferences({ ...this._current, ...changes });
    const changed = this.apply(next);
    await this.storage.save(this._current);
    if (changed) this.notify();
  }

  /** Restore the site defaults, persist, and notify listeners. */
  public async reset(): Promise<void> {
    await this.update({ ...defaultUserUIPreferences });
  }

  /**
   * Swap the persistence layer (e.g. to a backend-backed storage after login)
   * and re-read the preferences from it.
   */
  public async setStorage(storage: IUserPreferencesStorage): Promise<void> {
    this.storage = storage;
    await this.reload(true);
  }

  /** Re-read from storage. Sync loads (localStorage) apply before this returns. */
  public async reload(notify = true): Promise<void> {
    const apply = (loaded: Partial<UserUIPreferences> | null) => {
      // Only notify on a real change: setCurrentUser fires for the SAME user on
      // every token refresh (e.g. resume from sleep), and a redundant broadcast
      // would stomp transient per-table page sizes and reset tables to page 1.
      const changed = this.apply(sanitizeUserUIPreferences(loaded));
      if (notify && changed) this.notify();
    };
    const loaded = this.storage.load();
    if (loaded instanceof Promise) apply(await loaded);
    else apply(loaded);
  }

  /** Set _current; returns whether any field actually changed. */
  private apply(next: UserUIPreferences): boolean {
    const current = this._current;
    const changed =
      next.tableRowsPerPage !== current.tableRowsPerPage ||
      next.tabPlacement !== current.tabPlacement ||
      next.tabVariant !== current.tabVariant ||
      next.tableElevated !== current.tableElevated ||
      next.tableCellBorders !== current.tableCellBorders ||
      next.tableUppercaseHeader !== current.tableUppercaseHeader;
    this._current = next;
    return changed;
  }

  private notify() {
    window.dispatchEvent(
      new CustomEvent(userPreferencesChangedEventName, {
        detail: { preferences: this.current }
      }) satisfies UserPreferencesChangedEvent
    );
  }
}

/** The app-wide preferences store singleton. */
export const userUIPreferences = new UserPreferencesStore(new LocalUserPreferencesStorage());
